import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bot,
  Braces,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Eye,
  FileText,
  HelpCircle,
  Layers,
  Loader2,
  Maximize2,
  MessageCircle,
  MessageSquareText,
  Minimize2,
  MoreVertical,
  Pencil,
  Phone,
  PhoneCall,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Send,
  Settings2,
  Smartphone,
  Sparkles,
  Trash2,
  UserCheck,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ActionMenu } from "../../components/ui/action-menu";
import { Badge } from "../../components/ui/badge";
import { Dropdown } from "../../components/ui/dropdown";
import { useToast } from "../../components/ui/toast";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { cn } from "../../lib/utils";
import { whatsappApi, type WhatsAppInstance } from "../admin/whatsapp-api";
import {
  chatbotApi,
  chatbotError,
  type ChatbotAgent,
  type ChatbotConfig,
  type OutcomeField,
} from "./chatbot-api";

const input =
  "w-full rounded-lg border border-line bg-elevated px-3 py-2 text-sm text-ink outline-none transition placeholder:text-ink-3 focus:border-brand focus:ring-2 focus:ring-brand/20";
const primary =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-xs transition hover:bg-brand-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50";
const secondary =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-line bg-paper-card px-3 py-2 text-sm font-semibold text-ink shadow-2xs transition hover:border-brand/40 hover:bg-soft active:scale-[0.98] dark:bg-[#292524]";

const initial = (): ChatbotConfig => ({
  name: "",
  description: "",
  enabled: false,
  system_prompt: "",
  first_message: "",
  required_fields: [],
  outcome_fields: [],
});

const connectionLabel = (connection: WhatsAppInstance) =>
  connection.phone_number
    ? `+${connection.phone_number}`
    : `${connection.instance_name} (no number yet)`;

const PROMPT_STARTERS = [
  {
    id: "lead_qualifier",
    name: "Lead Qualifier Concierge",
    description: "Qualifies intent, collects budget, requirements, and timeline.",
    firstMessage:
      "Hello! Welcome to our WhatsApp service. How can I assist you with your requirements today?",
    systemPrompt: `You are an intelligent, courteous WhatsApp sales qualification assistant.
Your goal is to understand what the customer needs, explain our key offerings, and qualify their intent.

### Guidelines:
1. Keep replies friendly, concise, and easy to read on mobile screens (under 3 sentences per response).
2. Ask clear, one-question-at-a-time follow-ups to gather:
   - The customer's full name
   - Specific product or service of interest
   - Budget estimate
   - Timeline for implementation
3. Once the key details are confirmed, reassure the customer that our dedicated specialist will contact them shortly.`,
  },
  {
    id: "support_faq",
    name: "Customer Support & FAQ",
    description: "Diagnoses queries, resolves issues, and escalates when needed.",
    firstMessage:
      "Hi there! I'm your WhatsApp support assistant. How can I help resolve your query today?",
    systemPrompt: `You are the official 24/7 customer support assistant on WhatsApp.
Your role is to diagnose customer inquiries, provide clear troubleshooting steps, and answer common questions.

### Guidelines:
1. Always acknowledge customer questions with empathy and clarity.
2. Provide step-by-step solutions formatted with bullet points for mobile readability.
3. If the request requires human intervention or account access, collect their order/account reference and explain that our team has been notified.`,
  },
  {
    id: "appointment_booking",
    name: "Consultation & Booking",
    description: "Coordinates preferred dates, schedules calls, and confirms contact info.",
    firstMessage:
      "Hello! Looking to book a consultation or meeting with our team? I can help schedule that for you right now.",
    systemPrompt: `You are an AI appointment scheduling concierge for WhatsApp.
Your mission is to help customers pick a convenient consultation slot and capture their booking information.

### Guidelines:
1. Identify the topic or consultation service the customer desires.
2. Ask for their preferred day and time window (e.g. Morning: 10am-1pm, Afternoon: 2pm-5pm).
3. Confirm their contact phone number and email address for the calendar invite.
4. Provide a polite confirmation summary of the scheduled appointment.`,
  },
];

const OUTCOME_PRESETS = [
  { name: "customer_name", type: "string" as const, description: "Full name of the customer" },
  { name: "contact_number", type: "string" as const, description: "Phone number or WhatsApp contact" },
  { name: "budget_range", type: "number" as const, description: "Stated budget or spend capacity in numbers" },
  { name: "timeline", type: "string" as const, description: "Target timeline or expected start date" },
  { name: "qualified_lead", type: "boolean" as const, description: "Whether customer meets qualification criteria" },
  { name: "service_interest", type: "string" as const, description: "Specific product or service requested" },
];

function SimplePromptPreview({ content }: { content: string }) {
  if (!content.trim()) {
    return (
      <div className="flex min-h-[220px] items-center justify-center text-center text-xs text-ink-3">
        No system prompt written yet. Click 'Edit Raw' to compose instructions.
      </div>
    );
  }

  const lines = content.split("\n");
  return (
    <div
      aria-label="System prompt markdown preview"
      className="w-full overflow-auto rounded-lg border border-line bg-canvas p-5 text-sm leading-relaxed text-ink space-y-1.5"
    >
      {lines.map((line, idx) => {
        if (line.startsWith("### ")) {
          return (
            <h3 key={idx} className="mt-3 mb-1 text-sm font-bold text-ink border-b border-line/40 pb-1">
              {line.replace("### ", "")}
            </h3>
          );
        }
        if (line.startsWith("## ")) {
          return (
            <h2 key={idx} className="mt-4 mb-1.5 text-base font-bold text-ink border-b border-line pb-1">
              {line.replace("## ", "")}
            </h2>
          );
        }
        if (line.startsWith("# ")) {
          return (
            <h1 key={idx} className="mt-5 mb-2 text-lg font-bold text-ink">
              {line.replace("# ", "")}
            </h1>
          );
        }
        if (line.startsWith("- ") || line.startsWith("* ")) {
          return (
            <li key={idx} className="ml-4 list-disc text-xs text-ink-2">
              {line.slice(2)}
            </li>
          );
        }
        if (!line.trim()) {
          return <div key={idx} className="h-1.5" />;
        }
        return (
          <p key={idx} className="text-xs text-ink-2 whitespace-pre-wrap">
            {line}
          </p>
        );
      })}
    </div>
  );
}

export function ChatbotPage() {
  const { token, user } = useAuth() as any;
  const cache = useQueryClient();
  const toast = useToast();

  const clientId = user?.client_id || user?.id;
  const agents = useQuery({
    queryKey: ["chatbot-agents", token],
    queryFn: () => chatbotApi.agents(token),
    enabled: Boolean(token),
  });

  const [draft, setDraft] = useState<ChatbotConfig | null>(null);
  const [agentId, setAgentId] = useState<string>();
  const [tab, setTab] = useState<"prompt" | "outcomes" | "settings">("prompt");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [promptPreview, setPromptPreview] = useState(false);
  const [expandedStudio, setExpandedStudio] = useState(false);

  // List view search & filter state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingDeleteAgent, setPendingDeleteAgent] = useState<ChatbotAgent | null>(null);
  const [sortBy, setSortBy] = useState<"name" | "outcomes" | "prompt">("name");

  // WhatsApp connections query
  const connections = useQuery({
    queryKey: ["whatsapp-connections", token],
    queryFn: () => whatsappApi.listForClient(token, clientId),
    enabled: Boolean(token),
  });

  // Attached instance mapping
  const attachedConnection = connections.data?.find((c) => c.agent_id === agentId);
  const availableConnections = (connections.data ?? []).filter(
    (c) =>
      c.purposes?.includes("chatbot") &&
      (!c.agent_id || c.agent_id === clientId || c.agent_id === agentId),
  );
  const chatbotPurposeCount = (connections.data ?? []).filter((c) =>
    c.purposes?.includes("chatbot"),
  ).length;
  const attachedElsewhere = (connections.data ?? []).find(
    (c) =>
      c.purposes?.includes("chatbot") &&
      c.agent_id &&
      c.agent_id !== clientId &&
      c.agent_id !== agentId,
  );
  const attachedElsewhereName = attachedElsewhere
    ? agents.data?.find((a) => a.id === attachedElsewhere.agent_id)?.name
    : undefined;

  // Simulator state
  const [simMessages, setSimMessages] = useState<
    Array<{ id: string; sender: "bot" | "user"; text: string; time: string }>
  >([]);
  const [simInput, setSimInput] = useState("");

  // Sync simulator with draft first message
  useEffect(() => {
    if (draft) {
      setSimMessages([
        {
          id: "initial-msg",
          sender: "bot",
          text: draft.first_message || "Hello! How can I assist you today?",
          time: "10:30 AM",
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.name, agentId]);

  const handleSimSend = () => {
    if (!simInput.trim()) return;
    const userText = simInput.trim();
    const now = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const userMsg = { id: `u-${Date.now()}`, sender: "user" as const, text: userText, time: now };
    setSimMessages((prev) => [...prev, userMsg]);
    setSimInput("");

    // Simulated automated response
    setTimeout(() => {
      const botMsg = {
        id: `b-${Date.now()}`,
        sender: "bot" as const,
        text: `Understood! I've processed your message "${userText.length > 25 ? `${userText.slice(0, 25)}…` : userText}". As configured in "${draft?.name || "this agent"}", I'm ready to capture your details and assist.`,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      };
      setSimMessages((prev) => [...prev, botMsg]);
    }, 600);
  };

  const attachConnection = async (instanceName: string) => {
    if (!agentId || !instanceName || !draft) return;
    setAttaching(true);
    try {
      await whatsappApi.attachAgent(token, instanceName, agentId);
      if (!draft.enabled && draft.system_prompt.trim()) {
        const enabledAgent = await chatbotApi.save(token, { ...draft, enabled: true }, agentId);
        const { id: _id, ...enabledConfig } = enabledAgent;
        setDraft(enabledConfig);
        await cache.invalidateQueries({ queryKey: ["chatbot-agents", token] });
      }
      await connections.refetch();
      toast.success(
        draft.system_prompt.trim()
          ? "WhatsApp connection attached and AI replies enabled"
          : "WhatsApp connection attached",
      );
    } catch (err) {
      setError(chatbotError(err));
    } finally {
      setAttaching(false);
    }
  };

  const detachConnection = async () => {
    if (!attachedConnection) return;
    setAttaching(true);
    try {
      await whatsappApi.attachAgent(token, attachedConnection.instance_name, null);
      await connections.refetch();
      toast.success("WhatsApp connection detached");
    } catch (err) {
      setError(chatbotError(err));
    } finally {
      setAttaching(false);
    }
  };

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const patch = (part: Partial<ChatbotConfig>) => {
    setDraft((current) => current && { ...current, ...part });
    setDirty(true);
    setError("");
  };

  const open = (agent?: ChatbotAgent) => {
    if (agent) {
      const { id, ...config } = agent;
      const fields = [...config.outcome_fields];
      config.required_fields.forEach((name) => {
        if (!fields.some((field) => field.name === name)) {
          fields.push({ name, description: "", type: "string" });
        }
      });
      setAgentId(id);
      setDraft({ ...config, outcome_fields: fields });
    } else {
      setAgentId(undefined);
      setDraft(initial());
    }
    setTab("prompt");
    setDirty(false);
    setError("");
    setPromptPreview(false);
  };

  const close = () => {
    if (saving) return;
    if (dirty) {
      setShowDiscardConfirm(true);
      return;
    }
    setDraft(null);
    setDirty(false);
  };

  const updateField = (index: number, part: Partial<OutcomeField>) => {
    if (!draft) return;
    const old = draft.outcome_fields[index];
    patch({
      outcome_fields: draft.outcome_fields.map((field, i) =>
        i === index ? { ...field, ...part } : field,
      ),
      required_fields:
        part.name !== undefined
          ? draft.required_fields.map((name) => (name === old.name ? part.name! : name))
          : draft.required_fields,
    });
  };

  const addOutcome = () => {
    if (!draft || draft.outcome_fields.length >= 30) return;
    let n = draft.outcome_fields.length + 1;
    while (draft.outcome_fields.some((f) => f.name === `outcome_${n}`)) n++;
    patch({
      outcome_fields: [
        ...draft.outcome_fields,
        { name: `outcome_${n}`, type: "string", description: "" },
      ],
    });
  };

  const addOutcomePreset = (preset: { name: string; type: "string" | "number" | "boolean"; description: string }) => {
    if (!draft) return;
    if (draft.outcome_fields.some((f) => f.name === preset.name)) {
      toast.warning("Outcome exists", `"${preset.name}" is already configured.`);
      return;
    }
    patch({
      outcome_fields: [...draft.outcome_fields, { ...preset }],
      required_fields: [...draft.required_fields, preset.name],
    });
    toast.success("Outcome added", preset.name);
  };

  const applyPromptStarter = (starter: (typeof PROMPT_STARTERS)[0]) => {
    if (!draft) return;
    if (
      draft.system_prompt.trim() &&
      !window.confirm(`Replace current prompt with "${starter.name}" template?`)
    ) {
      return;
    }
    patch({
      system_prompt: starter.systemPrompt,
      first_message: starter.firstMessage,
    });
    toast.success("Starter applied", starter.name);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError("");
    try {
      const saved = await chatbotApi.save(token, draft, agentId);
      const { id, ...config } = saved;
      setDraft(config);
      setAgentId(id);
      setDirty(false);
      await cache.invalidateQueries({ queryKey: ["chatbot-agents", token] });
      toast.success("Chatbot agent saved", draft.name);
    } catch (err) {
      setError(chatbotError(err));
    } finally {
      setSaving(false);
    }
  };

  const [renameTarget, setRenameTarget] = useState<ChatbotAgent | null>(null);
  const [renameName, setRenameName] = useState("");
  const [renaming, setRenaming] = useState(false);

  const openRename = (agent: ChatbotAgent) => {
    setRenameTarget(agent);
    setRenameName(agent.name);
  };

  const confirmRename = async () => {
    if (!renameTarget) return;
    const name = renameName.trim();
    if (!name || name === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    setRenaming(true);
    try {
      const { id, ...config } = renameTarget;
      await chatbotApi.save(token, { ...config, name }, id);
      await cache.invalidateQueries({ queryKey: ["chatbot-agents", token] });
      toast.success("Agent renamed", name);
      setRenameTarget(null);
    } catch (err) {
      toast.danger(chatbotError(err));
    } finally {
      setRenaming(false);
    }
  };

  const duplicateAgent = async (agent: ChatbotAgent) => {
    try {
      const { id: _id, ...config } = agent;
      await chatbotApi.save(token, {
        ...config,
        name: `${agent.name} (Copy)`,
        enabled: false,
      });
      await cache.invalidateQueries({ queryKey: ["chatbot-agents", token] });
      toast.success("Agent duplicated", `${agent.name} (Copy)`);
    } catch (err) {
      toast.danger(chatbotError(err));
    }
  };

  const remove = async (agent: ChatbotAgent) => {
    setPendingDeleteAgent(agent);
  };

  const executeDelete = async (agent: ChatbotAgent) => {
    try {
      await chatbotApi.delete(token, agent.id);
      await cache.invalidateQueries({ queryKey: ["chatbot-agents", token] });
      toast.success("Agent deleted");
    } catch (err) {
      toast.danger(chatbotError(err));
    } finally {
      setPendingDeleteAgent(null);
    }
  };

  // Filtered & sorted agents list
  const filteredAgents = useMemo(() => {
    const list = (agents.data ?? []).filter((agent) => {
      if (statusFilter === "active" && !agent.enabled) return false;
      if (statusFilter === "inactive" && agent.enabled) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase().trim();
      return (
        agent.name.toLowerCase().includes(q) ||
        (agent.description || "").toLowerCase().includes(q) ||
        agent.id.toLowerCase().includes(q) ||
        agent.system_prompt.toLowerCase().includes(q)
      );
    });

    list.sort((a, b) => {
      if (sortBy === "outcomes") {
        return (
          b.outcome_fields.length +
          b.required_fields.length -
          (a.outcome_fields.length + a.required_fields.length)
        );
      }
      if (sortBy === "prompt") {
        return (b.system_prompt || "").length - (a.system_prompt || "").length;
      }
      return a.name.localeCompare(b.name);
    });

    return list;
  }, [agents.data, search, statusFilter, sortBy]);

  // Overall counts
  const totalAgents = agents.data?.length ?? 0;
  const activeAgents = agents.data?.filter((a) => a.enabled).length ?? 0;

  // ════════════════════════════════════════════════════════════════════════
  // ── 1. AGENT STUDIO VIEW (Create / Edit Agent) ──
  // ════════════════════════════════════════════════════════════════════════
  if (draft) {
    return (
      <div className="space-y-4 px-4 pb-8 pt-3 sm:px-5 sm:pt-4 md:px-6">
        {/* ── Studio Top Header (Modeled after SingleAgentStudio) ── */}
        <header className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line/60 bg-paper-card p-4 shadow-card dark:bg-[#292524] dark:shadow-card-dark">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className={secondary}
              onClick={close}
              aria-label="Back to chatbot agents"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Agents</span>
            </button>

            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-soft text-brand font-bold text-sm">
                <Bot className="h-5 w-5" />
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-sm font-bold text-ink sm:text-base">
                    {draft.name.trim() || "New Chatbot Agent"}
                  </h1>
                  <Badge tone={draft.enabled ? "success" : "neutral"}>
                    {draft.enabled ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#16a34a] animate-pulse" />
                        Active
                      </span>
                    ) : (
                      "Draft"
                    )}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-xs text-ink-3">
                  {agentId ? (
                    <span className="font-mono text-[10px]">{agentId}</span>
                  ) : (
                    <span>Unsaved draft</span>
                  )}
                  {dirty && (
                    <span className="inline-flex items-center gap-1 font-medium text-amber-600 dark:text-amber-400">
                      • <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> Unsaved changes
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setExpandedStudio((curr) => !curr)}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-line bg-paper-card px-3 py-2 text-xs font-semibold text-ink hover:border-brand/40 dark:bg-[#292524]"
              title={expandedStudio ? "Show phone simulator" : "Focus on studio editor"}
            >
              {expandedStudio ? (
                <>
                  <Minimize2 className="h-3.5 w-3.5" /> Show Preview
                </>
              ) : (
                <>
                  <Maximize2 className="h-3.5 w-3.5" /> Focus Mode
                </>
              )}
            </button>

            <button
              type="button"
              className={primary}
              disabled={saving || !draft.name.trim() || (draft.enabled && !draft.system_prompt.trim())}
              onClick={save}
              aria-label="Save agent"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving ? "Saving…" : "Save agent"}
            </button>
          </div>
        </header>

        {error && (
          <p role="alert" className="rounded-xl border border-danger/30 bg-danger-soft p-3 text-sm text-danger shadow-sm">
            {error}
          </p>
        )}

        {/* ── Studio Main Layout ── */}
        <fieldset disabled={saving} className="grid min-w-0 gap-6 lg:grid-cols-12">
          {/* ── Left / Main Studio Column ── */}
          <div className={cn("min-w-0 space-y-4", expandedStudio ? "lg:col-span-12" : "lg:col-span-8")}>
            {/* Section Tabs Navigation */}
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-line/60 bg-paper-card p-1.5 shadow-2xs dark:bg-[#292524]">
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setTab("prompt")}
                  className={cn(
                    "flex items-center gap-2 rounded-lg px-3.5 py-2 text-xs font-semibold transition",
                    tab === "prompt"
                      ? "bg-brand text-white shadow-xs"
                      : "text-ink-2 hover:bg-soft",
                  )}
                >
                  <FileText className="h-3.5 w-3.5" />
                  Prompt & First Message
                </button>

                <button
                  type="button"
                  aria-label="Post-chat data"
                  onClick={() => setTab("outcomes")}
                  className={cn(
                    "flex items-center gap-2 rounded-lg px-3.5 py-2 text-xs font-semibold transition",
                    tab === "outcomes"
                      ? "bg-brand text-white shadow-xs"
                      : "text-ink-2 hover:bg-soft",
                  )}
                >
                  <Braces className="h-3.5 w-3.5" />
                  Post-chat data
                  {draft.outcome_fields.length > 0 && (
                    <span
                      className={cn(
                        "rounded-full px-1.5 py-0.2 text-[10px] font-mono",
                        tab === "outcomes"
                          ? "bg-white/20 text-white"
                          : "bg-soft text-ink-3",
                      )}
                    >
                      {draft.outcome_fields.length}
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setTab("settings")}
                  className={cn(
                    "flex items-center gap-2 rounded-lg px-3.5 py-2 text-xs font-semibold transition",
                    tab === "settings"
                      ? "bg-brand text-white shadow-xs"
                      : "text-ink-2 hover:bg-soft",
                  )}
                >
                  <Smartphone className="h-3.5 w-3.5" />
                  WhatsApp & Settings
                </button>
              </div>

              {/* Status Indicator */}
              <div className="hidden pr-2 text-[11px] text-ink-3 md:block">
                <span>
                  {draft.system_prompt.length.toLocaleString()} chars • {draft.outcome_fields.length} outcomes
                </span>
              </div>
            </div>

            {/* ── TAB 1: PROMPT & FIRST MESSAGE ── */}
            {tab === "prompt" && (
              <div className="space-y-4">
                {/* First Message Card */}
                <section className="overflow-hidden rounded-xl border border-line/60 bg-paper-card shadow-card dark:bg-[#292524] dark:shadow-card-dark">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line/60 px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <MessageSquareText className="h-4 w-4 text-brand" />
                      <div>
                        <h2 className="text-xs font-bold text-ink sm:text-sm">First Message / Greeting</h2>
                        <p className="text-[11px] text-ink-3">
                          Sent immediately when a recipient opens a new chat session.
                        </p>
                      </div>
                    </div>
                    <span className="font-mono text-[10px] text-ink-3">
                      {(draft.first_message || "").length} / 1,000 chars
                    </span>
                  </div>

                  <div className="p-4 sm:p-5">
                    <textarea
                      id="bot-first"
                      maxLength={1000}
                      rows={2}
                      value={draft.first_message}
                      onChange={(e) => patch({ first_message: e.target.value })}
                      placeholder="e.g. Hello! Welcome to TechLadder. How can I assist you with your requirements today?"
                      className="block w-full rounded-lg border border-line bg-paper p-3 text-sm text-ink outline-none transition placeholder:text-ink-3 focus:border-brand dark:bg-[#1f1c1a]"
                    />

                    {/* Greeting Suggestions */}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-medium text-ink-3">Quick starters:</span>
                      <button
                        type="button"
                        onClick={() =>
                          patch({
                            first_message:
                              "Hello! Welcome to our WhatsApp service. How can I assist you today?",
                          })
                        }
                        className="rounded-md border border-line bg-soft/50 px-2 py-1 text-[11px] text-ink transition hover:border-brand/40 hover:bg-soft"
                      >
                        👋 Welcome
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          patch({
                            first_message:
                              "Hi there! Thanks for reaching out. What product or service are you interested in?",
                          })
                        }
                        className="rounded-md border border-line bg-soft/50 px-2 py-1 text-[11px] text-ink transition hover:border-brand/40 hover:bg-soft"
                      >
                        🎯 Lead Inquiry
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          patch({
                            first_message:
                              "Hello! I am your 24/7 AI assistant. How can I help resolve your questions?",
                          })
                        }
                        className="rounded-md border border-line bg-soft/50 px-2 py-1 text-[11px] text-ink transition hover:border-brand/40 hover:bg-soft"
                      >
                        🛠️ Support
                      </button>
                    </div>
                  </div>
                </section>

                {/* System Prompt Card */}
                <section className="overflow-hidden rounded-xl border border-line/60 bg-paper-card shadow-card dark:bg-[#292524] dark:shadow-card-dark">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line/60 px-5 py-3.5">
                    <div>
                      <label htmlFor="bot-prompt" className="text-xs font-bold text-ink sm:text-sm">
                        System Prompt
                      </label>
                      <p className="text-[11px] text-ink-3">
                        The operating contract defining tone, qualification criteria, and response logic.
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-[10px] text-ink-3">
                        {draft.system_prompt.length.toLocaleString()} / 20,000 chars
                      </span>

                      <button
                        type="button"
                        onClick={() => setPromptPreview((curr) => !curr)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-line bg-paper px-2.5 py-1 text-xs font-semibold text-ink transition hover:border-brand dark:bg-[#1f1c1a]"
                      >
                        {promptPreview ? (
                          <>
                            <Pencil className="h-3 w-3" /> Edit Raw
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-3 w-3" /> Markdown Preview
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Template Starters Bar */}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line/50 bg-soft/40 px-5 py-2.5 text-xs">
                    <span className="font-medium text-ink-2">Insert verified template:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {PROMPT_STARTERS.map((starter) => (
                        <button
                          key={starter.id}
                          type="button"
                          onClick={() => applyPromptStarter(starter)}
                          className="rounded-md border border-line bg-paper-card px-2.5 py-1 text-[11px] font-medium text-ink transition hover:border-brand hover:text-brand dark:bg-[#292524]"
                        >
                          {starter.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {promptPreview ? (
                    <div className="p-5">
                      <SimplePromptPreview content={draft.system_prompt} />
                    </div>
                  ) : (
                    <textarea
                      id="bot-prompt"
                      aria-label="System prompt"
                      maxLength={20000}
                      rows={16}
                      value={draft.system_prompt}
                      onChange={(e) => patch({ system_prompt: e.target.value })}
                      placeholder="Define the agent's role, conversation flow, factual boundaries, escalation rules, and tool usage..."
                      className="block w-full resize-y border-0 bg-paper p-5 font-mono text-xs leading-6 text-ink outline-none focus:ring-2 focus:ring-inset focus:ring-brand/30 dark:bg-[#1f1c1a] sm:text-sm"
                    />
                  )}

                  <div className="flex items-center justify-between border-t border-line/60 bg-soft/50 px-5 py-2 text-xs text-ink-3">
                    <span>
                      {promptPreview ? "Markdown preview mode active" : "Markdown formatted instructions supported"}
                    </span>
                    <span>
                      {draft.system_prompt.trim().length > 0 ? (
                        <span className="inline-flex items-center gap-1 text-[#16a34a]">
                          <CheckCircle2 className="h-3 w-3" /> Prompt configured
                        </span>
                      ) : (
                        <span className="text-amber-600 dark:text-amber-400">Prompt required for AI replies</span>
                      )}
                    </span>
                  </div>
                </section>
              </div>
            )}

            {/* ── TAB 2: POST-CHAT DATA & OUTCOMES ── */}
            {tab === "outcomes" && (
              <section className="rounded-xl border border-line/60 bg-paper-card p-4 shadow-card dark:bg-[#292524] dark:shadow-card-dark sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line/60 pb-4">
                  <div>
                    <h2 className="text-sm font-bold text-ink sm:text-base">Conversation Outcomes</h2>
                    <p className="mt-1 max-w-xl text-xs text-ink-3 sm:text-sm">
                      Choose structured data variables to extract from chats. Required fields guide qualification;
                      optional fields are recorded whenever provided by the customer.
                    </p>
                  </div>

                  <button
                    type="button"
                    className={secondary}
                    disabled={draft.outcome_fields.length >= 30}
                    onClick={addOutcome}
                    aria-label="Add outcome"
                  >
                    <Plus className="h-4 w-4" />
                    Add outcome
                  </button>
                </div>

                {/* Quick Add Presets */}
                <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg bg-soft/50 p-2.5 text-xs">
                  <span className="font-semibold text-ink-2">Common fields:</span>
                  {OUTCOME_PRESETS.map((preset) => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => addOutcomePreset(preset)}
                      className="inline-flex items-center gap-1 rounded-md border border-line bg-paper px-2 py-1 text-[11px] font-medium text-ink transition hover:border-brand hover:text-brand dark:bg-[#1f1c1a]"
                    >
                      <Plus className="h-3 w-3 text-brand" /> {preset.name}
                    </button>
                  ))}
                </div>

                {/* Outcome Fields List */}
                <div className="mt-5 space-y-3">
                  {draft.outcome_fields.map((field, index) => (
                    <div
                      key={index}
                      className="space-y-3 rounded-xl border border-line/70 bg-soft/30 p-4 transition hover:border-brand/40"
                    >
                      <div className="grid items-end gap-3 sm:grid-cols-[1fr_140px_auto]">
                        <label className="text-xs font-semibold text-ink-2">
                          Field name
                          <input
                            aria-label={`Field name ${index + 1}`}
                            className={`${input} mt-1`}
                            value={field.name}
                            onChange={(e) => updateField(index, { name: e.target.value })}
                            placeholder="e.g. preferred_location"
                          />
                        </label>

                        <div className="text-xs font-semibold text-ink-2">
                          <span className="block mb-1">Type</span>
                          <Dropdown
                            ariaLabel={`Field ${index + 1} type`}
                            size="sm"
                            value={field.type}
                            onChange={(v) =>
                              updateField(index, {
                                type: v as OutcomeField["type"],
                              })
                            }
                            options={[
                              { value: "string", label: "Text" },
                              { value: "number", label: "Number" },
                              { value: "boolean", label: "Yes / No" },
                            ]}
                          />
                          <select
                            aria-label="Type"
                            className="sr-only"
                            tabIndex={-1}
                            value={field.type}
                            onChange={(e) =>
                              updateField(index, {
                                type: e.target.value as OutcomeField["type"],
                              })
                            }
                          >
                            <option value="string">Text</option>
                            <option value="number">Number</option>
                            <option value="boolean">Yes / No</option>
                          </select>
                        </div>

                        <button
                          type="button"
                          className={`${secondary} mb-0.5 text-[#ef4444] hover:bg-danger-soft hover:text-danger`}
                          aria-label={`Remove ${field.name}`}
                          onClick={() =>
                            patch({
                              outcome_fields: draft.outcome_fields.filter((_, i) => i !== index),
                              required_fields: draft.required_fields.filter(
                                (name) => name !== field.name,
                              ),
                            })
                          }
                          title="Remove outcome field"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <label className="block text-xs font-semibold text-ink-2">
                        Extraction instructions
                        <input
                          className={`${input} mt-1`}
                          value={field.description}
                          maxLength={1000}
                          onChange={(e) => updateField(index, { description: e.target.value })}
                          placeholder="Describe what the AI should capture from the conversation"
                        />
                      </label>

                      <div className="flex items-center justify-between border-t border-line/40 pt-2">
                        <label className="flex items-center gap-2 text-xs font-medium text-ink-2 cursor-pointer">
                          <input
                            type="checkbox"
                            aria-label="Required for qualification"
                            className="h-4 w-4 rounded border-line accent-brand"
                            checked={draft.required_fields.includes(field.name)}
                            onChange={(e) =>
                              patch({
                                required_fields: e.target.checked
                                  ? [...draft.required_fields, field.name]
                                  : draft.required_fields.filter((name) => name !== field.name),
                              })
                            }
                          />
                          Required for lead qualification
                        </label>

                        <span className="font-mono text-[10px] text-ink-3 uppercase">
                          {field.type}
                        </span>
                      </div>
                    </div>
                  ))}

                  {draft.outcome_fields.length === 0 && (
                    <div className="rounded-xl border border-dashed border-line p-10 text-center">
                      <Braces className="mx-auto h-8 w-8 text-ink-3 opacity-60" />
                      <p className="mt-2 text-sm font-semibold text-ink">No outcomes added yet</p>
                      <p className="mt-1 text-xs text-ink-3">
                        Add structured outcome fields or click common presets above to capture lead variables.
                      </p>
                      <button
                        type="button"
                        onClick={addOutcome}
                        className={`${secondary} mt-4 text-xs`}
                      >
                        <Plus className="h-3.5 w-3.5" /> Add Outcome
                      </button>
                    </div>
                  )}
                </div>

                <p className="mt-4 text-xs text-ink-3">
                  Tip: Use lowercase snake_case for field names, such as{" "}
                  <code className="rounded bg-soft px-1.5 py-0.5 font-mono text-[11px]">customer_name</code>,{" "}
                  <code className="rounded bg-soft px-1.5 py-0.5 font-mono text-[11px]">budget</code>, or{" "}
                  <code className="rounded bg-soft px-1.5 py-0.5 font-mono text-[11px]">timeline</code>.
                </p>
              </section>
            )}

            {/* ── TAB 3: WHATSAPP & SETTINGS ── */}
            {tab === "settings" && (
              <div className="space-y-4">
                {/* WhatsApp Phone Connection Card */}
                <section className="rounded-xl border border-line/60 bg-paper-card p-5 shadow-card dark:bg-[#292524] dark:shadow-card-dark">
                  <div className="flex items-center gap-2 border-b border-line/60 pb-3">
                    <Smartphone className="h-4 w-4 text-brand" />
                    <div>
                      <h2 className="text-sm font-bold text-ink">WhatsApp Phone Connection</h2>
                      <p className="text-xs text-ink-3">
                        Route incoming WhatsApp messages on this number to this AI agent.
                      </p>
                    </div>
                  </div>

                  <div className="mt-4">
                    {!agentId ? (
                      <div className="rounded-lg bg-soft p-4 text-xs text-ink-3">
                        <p className="font-medium text-ink">Save this agent first</p>
                        <p className="mt-0.5">
                          Once saved, you can pair this agent to a dedicated WhatsApp business number.
                        </p>
                      </div>
                    ) : attachedConnection ? (
                      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-50/60 p-4 dark:bg-emerald-950/20">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#16a34a] text-white">
                            <Smartphone className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-bold text-ink">{connectionLabel(attachedConnection)}</p>
                            <p className="text-xs text-[#16a34a] font-medium">
                              ● Connected & answering incoming chats
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          className={`${secondary} py-1.5 text-xs text-[#ef4444] hover:bg-danger-soft`}
                          disabled={attaching}
                          onClick={detachConnection}
                        >
                          {attaching ? "Detaching…" : "Detach number"}
                        </button>
                      </div>
                    ) : availableConnections.length > 0 ? (
                      <div className="space-y-3">
                        <label className="block text-xs font-semibold text-ink">
                          Select Available WhatsApp Number
                        </label>
                        <Dropdown
                          ariaLabel="WhatsApp connection"
                          value=""
                          disabled={attaching || connections.isLoading}
                          options={availableConnections.map((conn) => ({
                            value: conn.instance_name,
                            label: connectionLabel(conn),
                          }))}
                          onChange={attachConnection}
                          className="bg-elevated"
                        />
                        <p className="text-xs text-ink-3">
                          Selecting a number attaches it immediately and enables autonomous AI replies.
                        </p>
                      </div>
                    ) : !connections.isLoading ? (
                      chatbotPurposeCount > 0 ? (
                        <div className="rounded-lg bg-amber-50 p-4 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                          {attachedElsewhere ? (
                            <p>
                              Your WhatsApp numbers are currently attached to{" "}
                              <strong>{attachedElsewhereName ? `"${attachedElsewhereName}"` : "another agent"}</strong>.
                              Detach it there first to attach it here.
                            </p>
                          ) : (
                            <p>
                              No free chatbot number available. Detach your current agent or register a new number in Settings.
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="rounded-lg border border-line bg-soft/50 p-4 text-xs text-ink-3">
                          <p>
                            No WhatsApp number enabled for Chatbot purpose. Turn on WhatsApp Chatbot for a number in{" "}
                            <Link className="font-semibold text-brand underline" to="/settings?section=whatsapp">
                              Settings → WhatsApp
                            </Link>{" "}
                            first.
                          </p>
                        </div>
                      )
                    ) : null}

                    {connections.isError && (
                      <p className="mt-3 text-xs text-danger">{chatbotError(connections.error)}</p>
                    )}
                  </div>
                </section>

                {/* Identity & Metadata Card */}
                <section className="rounded-xl border border-line/60 bg-paper-card p-5 shadow-card dark:bg-[#292524] dark:shadow-card-dark">
                  <div className="flex items-center gap-2 border-b border-line/60 pb-3">
                    <Settings2 className="h-4 w-4 text-ink-3" />
                    <h2 className="text-sm font-bold text-ink">Agent Metadata</h2>
                  </div>

                  <div className="mt-4 space-y-4">
                    <label className="block text-xs font-semibold text-ink">
                      Agent Name
                      <input
                        className={`${input} mt-1`}
                        maxLength={100}
                        value={draft.name}
                        onChange={(e) => patch({ name: e.target.value })}
                        placeholder="e.g. Sales Assistant"
                      />
                    </label>

                    <label className="block text-xs font-semibold text-ink">
                      Description & Business Objective
                      <textarea
                        className={`${input} mt-1`}
                        rows={3}
                        maxLength={1000}
                        value={draft.description}
                        onChange={(e) => patch({ description: e.target.value })}
                        placeholder="Describe what tasks this agent solves for your customers..."
                      />
                    </label>
                  </div>
                </section>
              </div>
            )}
          </div>

          {/* ── Right Column: Interactive WhatsApp Simulator Preview ── */}
          {!expandedStudio && (
            <aside className="space-y-4 lg:col-span-4">
              {/* WhatsApp Phone Mockup */}
              <div className="overflow-hidden rounded-2xl border border-line/80 bg-paper-card shadow-card dark:bg-[#292524] dark:shadow-card-dark">
                {/* Phone Header Bar (WhatsApp Green) */}
                <div className="flex items-center justify-between bg-[#075e54] px-4 py-3 text-white">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white font-bold text-xs">
                      <Bot className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-xs font-bold leading-tight">
                        {draft.name.trim() || "AI Assistant"}
                      </p>
                      <p className="flex items-center gap-1 text-[10px] text-emerald-200">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                        online
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-white/80">
                    <Phone className="h-3.5 w-3.5" />
                    <MoreVertical className="h-3.5 w-3.5" />
                  </div>
                </div>

                {/* Chat Wallpaper Canvas */}
                <div className="flex h-[340px] flex-col justify-between bg-[#e5ddd5]/30 p-3.5 dark:bg-[#181615]">
                  {/* Messages Scroll Area */}
                  <div className="space-y-2.5 overflow-y-auto pr-1 text-xs">
                    <div className="my-1 text-center">
                      <span className="rounded-md bg-paper/80 px-2 py-0.5 text-[10px] font-medium text-ink-3 shadow-2xs dark:bg-black/40">
                        TODAY
                      </span>
                    </div>

                    {simMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={cn("flex", msg.sender === "user" ? "justify-end" : "justify-start")}
                      >
                        <div
                          className={cn(
                            "max-w-[85%] rounded-lg px-3 py-2 text-xs shadow-xs",
                            msg.sender === "user"
                              ? "bg-[#d9fdd3] text-[#111b21] dark:bg-[#005c4b] dark:text-white"
                              : "bg-paper-card text-ink dark:bg-[#202c33]",
                          )}
                        >
                          <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                          <div className="mt-1 flex items-center justify-end gap-1 text-[9px] text-ink-3">
                            <span>{msg.time}</span>
                            {msg.sender === "user" && <span className="text-[#53bdeb]">✓✓</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Simulator Interactive Input */}
                  <div className="mt-2 flex items-center gap-1.5 rounded-full border border-line/60 bg-paper px-3 py-1.5 shadow-2xs dark:bg-[#202c33]">
                    <input
                      type="text"
                      placeholder="Type a test customer message…"
                      value={simInput}
                      onChange={(e) => setSimInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSimSend();
                      }}
                      className="min-w-0 flex-1 bg-transparent text-xs text-ink outline-none placeholder:text-ink-3"
                    />
                    <button
                      type="button"
                      onClick={handleSimSend}
                      disabled={!simInput.trim()}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#075e54] text-white disabled:opacity-30"
                    >
                      <Send className="h-3 w-3" />
                    </button>
                  </div>
                </div>

                {/* Simulator Footer Controls */}
                <div className="flex items-center justify-between border-t border-line/60 bg-paper-card px-4 py-2.5 text-xs text-ink-3 dark:bg-[#292524]">
                  <span className="text-[11px]">Interactive WhatsApp Simulator</span>
                  <button
                    type="button"
                    onClick={() =>
                      setSimMessages([
                        {
                          id: `init-${Date.now()}`,
                          sender: "bot",
                          text: draft.first_message || "Hello! How can I assist you today?",
                          time: "10:30 AM",
                        },
                      ])
                    }
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand hover:underline"
                  >
                    <RotateCcw className="h-3 w-3" /> Reset
                  </button>
                </div>
              </div>

              {/* Extracted Variables Real-Time Card */}
              <div className="rounded-xl border border-line/60 bg-paper-card p-4 shadow-card dark:bg-[#292524] dark:shadow-card-dark">
                <div className="flex items-center justify-between border-b border-line/60 pb-2.5">
                  <div className="flex items-center gap-1.5">
                    <Braces className="h-3.5 w-3.5 text-brand" />
                    <h3 className="text-xs font-bold text-ink">Extracted Variables</h3>
                  </div>
                  <span className="font-mono text-[10px] text-ink-3">
                    {draft.outcome_fields.length} outcomes
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {draft.outcome_fields.map((f) => (
                    <span
                      key={f.name}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-mono",
                        draft.required_fields.includes(f.name)
                          ? "bg-brand-soft text-brand font-semibold"
                          : "bg-soft text-ink-2",
                      )}
                    >
                      {f.name}
                      {draft.required_fields.includes(f.name) && (
                        <span className="text-[9px] font-bold text-brand">*</span>
                      )}
                    </span>
                  ))}
                  {draft.outcome_fields.length === 0 && (
                    <span className="text-xs text-ink-3">No variables configured yet.</span>
                  )}
                </div>

                {agentId && (
                  <div className="mt-3 border-t border-line/50 pt-2.5">
                    <Link
                      to={`/whatsapp/conversations?agent=${encodeURIComponent(agentId)}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                    >
                      View Live Chat History <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                )}
              </div>
            </aside>
          )}
        </fieldset>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // ── 2. AGENTS HUB / LIST PAGE VIEW (Revamped) ──
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6 px-4 pb-8 pt-3 sm:px-5 sm:pt-4 md:px-6">
      {/* ── Top Hero Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-sm font-semibold text-ink">
              WhatsApp AI Agents
            </h1>
            <Badge tone="neutral">{totalAgents} agents</Badge>
          </div>
        </div>

        <button
          type="button"
          className={primary}
          onClick={() => open()}
          aria-label="Create agent"
        >
          <Plus className="h-4 w-4" />
          Create agent
        </button>
      </div>

      {/* ── Search & Filter Controls ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line/60 bg-paper-card p-3 shadow-card dark:bg-[#292524] dark:shadow-card-dark">
        {/* Search */}
        <div className="relative min-w-[240px] flex-1 sm:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
          <input
            type="text"
            placeholder="Search agents by name, description, prompt..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-full rounded-lg border border-line bg-paper pl-9 pr-3 text-xs text-ink outline-none transition placeholder:text-ink-3 focus:border-brand dark:bg-[#1f1c1a]"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter Tabs */}
          <div className="flex items-center rounded-lg border border-line bg-soft/50 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setStatusFilter("all")}
              className={cn(
                "rounded-md px-3 py-1 font-medium transition",
                statusFilter === "all"
                  ? "bg-paper-card text-ink shadow-sm dark:bg-[#292524]"
                  : "text-ink-3 hover:text-ink",
              )}
            >
              All ({totalAgents})
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("active")}
              className={cn(
                "rounded-md px-3 py-1 font-medium transition",
                statusFilter === "active"
                  ? "bg-paper-card text-ink shadow-sm dark:bg-[#292524]"
                  : "text-ink-3 hover:text-ink",
              )}
            >
              Active ({activeAgents})
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter("inactive")}
              className={cn(
                "rounded-md px-3 py-1 font-medium transition",
                statusFilter === "inactive"
                  ? "bg-paper-card text-ink shadow-sm dark:bg-[#292524]"
                  : "text-ink-3 hover:text-ink",
              )}
            >
              Inactive ({totalAgents - activeAgents})
            </button>
          </div>

          {/* Sort By Dropdown */}
          <div className="w-44 shrink-0">
            <Dropdown
              ariaLabel="Sort agents"
              size="sm"
              value={sortBy}
              onChange={(v) => setSortBy(v as any)}
              options={[
                { value: "name", label: "Sort: Name (A-Z)" },
                { value: "outcomes", label: "Sort: Most Outcomes" },
                { value: "prompt", label: "Sort: Prompt Length" },
              ]}
            />
          </div>
        </div>
      </div>

      {/* ── Loading & Error States ── */}
      {agents.isLoading && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-brand" />
          <p className="mt-3 text-sm text-ink-3">Loading your WhatsApp AI agents…</p>
        </div>
      )}

      {agents.isError && (
        <div
          role="alert"
          className="rounded-xl border border-danger/30 bg-danger-soft p-5 text-sm text-danger shadow-sm"
        >
          <p className="font-semibold">{chatbotError(agents.error)}</p>
          <button
            type="button"
            className="mt-2 inline-flex items-center gap-1 font-semibold underline"
            onClick={() => agents.refetch()}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Try again
          </button>
        </div>
      )}

      {/* ── Agents Cards Grid ── */}
      {!agents.isLoading && !agents.isError && (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {filteredAgents.map((agent) => {
            const attachedConn = connections.data?.find((c) => c.agent_id === agent.id);
            const outcomesCount = new Set([
              ...agent.required_fields,
              ...agent.outcome_fields.map((f) => f.name),
            ]).size;

            return (
              <article
                key={agent.id}
                onClick={() => open(agent)}
                className="group relative flex flex-col justify-between rounded-2xl border border-line/60 bg-paper-card p-5 shadow-card transition-all duration-200 hover:-translate-y-1 hover:border-brand/40 hover:shadow-card-hover dark:bg-[#292524] dark:shadow-card-dark cursor-pointer"
              >
                <div>
                  {/* Top Header Row */}
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      className="flex items-start gap-3 text-left min-w-0"
                      onClick={() => open(agent)}
                    >
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand/20 to-brand/5 text-brand border border-brand/20 shadow-xs">
                        <Bot className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <h2
                          className="truncate text-base font-bold text-ink transition group-hover:text-brand"
                          title={agent.name}
                        >
                          {agent.name}
                        </h2>
                        <p className="font-mono text-[10px] text-ink-3">{agent.id}</p>
                      </div>
                    </button>

                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <Badge tone={agent.enabled ? "success" : "neutral"}>
                        {agent.enabled ? (
                          <span className="inline-flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#16a34a] animate-pulse" />
                            Active
                          </span>
                        ) : (
                          "Inactive"
                        )}
                      </Badge>

                      <ActionMenu
                        ariaLabel={`${agent.name} actions`}
                        items={[
                          {
                            label: "Rename agent",
                            icon: <Pencil className="h-4 w-4" />,
                            onClick: () => openRename(agent),
                          },
                          {
                            label: "Duplicate agent",
                            icon: <Copy className="h-4 w-4" />,
                            onClick: () => duplicateAgent(agent),
                          },
                          {
                            label: "Delete agent",
                            icon: <Trash2 className="h-4 w-4" />,
                            danger: true,
                            onClick: () => remove(agent),
                          },
                        ]}
                      />
                    </div>
                  </div>

                  {/* Connected WhatsApp Number Pill */}
                  <div className="mt-3">
                    {attachedConn ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-[#16a34a] dark:bg-emerald-950/40">
                        <Smartphone className="h-3.5 w-3.5" />
                        {connectionLabel(attachedConn)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-soft px-2.5 py-0.5 text-xs text-ink-3">
                        <Smartphone className="h-3.5 w-3.5 opacity-50" />
                        No number attached
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <p className="mt-2.5 line-clamp-2 min-h-[2.5rem] text-xs leading-relaxed text-ink-2">
                    {agent.description || "Autonomous WhatsApp conversational assistant."}
                  </p>

                  {/* Telemetry Strip (Modeled after Voice AgentCard) */}
                  <div className="my-3.5 grid grid-cols-3 divide-x divide-line/60 rounded-xl border border-line/60 bg-soft/30 py-2.5 text-center text-xs">
                    <div className="px-1">
                      <span className="block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
                        Prompt
                      </span>
                      <strong className="mt-0.5 block font-mono text-sm text-ink">
                        {(agent.system_prompt || "").length.toLocaleString()}
                        <span className="text-[10px] font-normal text-ink-3 font-sans"> ch</span>
                      </strong>
                    </div>

                    <div className="px-1">
                      <span className="block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
                        Required
                      </span>
                      <strong className="mt-0.5 block font-mono text-sm text-ink">
                        {agent.required_fields.length}
                        <span className="text-[10px] font-normal text-ink-3 font-sans"> fields</span>
                      </strong>
                    </div>

                    <div className="px-1">
                      <span className="block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
                        Outcomes
                      </span>
                      <strong className="mt-0.5 block font-mono text-sm text-[#8b5cf6]">
                        {outcomesCount}
                        <span className="text-[10px] font-normal text-ink-3 font-sans"> total</span>
                      </strong>
                    </div>
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex flex-wrap items-center gap-2 border-t border-line/50 pt-3">
                  <button
                    type="button"
                    className={`${primary} flex-1 text-xs`}
                    onClick={() => open(agent)}
                    aria-label="Open agent"
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                    Open agent
                  </button>

                  <Link
                    to={`/whatsapp/conversations?agent=${encodeURIComponent(agent.id)}`}
                    onClick={(e) => e.stopPropagation()}
                    className={`${secondary} text-xs`}
                    aria-label="My Chat"
                    title="Open live conversation history for this agent"
                  >
                    <MessageCircle className="h-3.5 w-3.5 text-brand" />
                    My Chat
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* ── Empty State ── */}
      {!agents.isLoading && !agents.isError && filteredAgents.length === 0 && (
        <div className="rounded-2xl border border-dashed border-line bg-paper-card p-12 text-center shadow-card dark:bg-[#292524]">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-soft text-brand">
            <Bot className="h-7 w-7" />
          </div>

          <h2 className="mt-4 text-base font-bold text-ink sm:text-lg">
            {search.trim() ? "No agents matching your search" : "Create your first WhatsApp AI agent"}
          </h2>

          <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-ink-3 sm:text-sm">
            {search.trim()
              ? `No agents found matching "${search}". Try clearing your search filter.`
              : "Set up a prompt contract, first greeting message, and lead qualification outcomes, then connect a WhatsApp business number."}
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {search.trim() ? (
              <button
                type="button"
                className={secondary}
                onClick={() => setSearch("")}
              >
                Clear Search
              </button>
            ) : (
              <button
                type="button"
                className={primary}
                onClick={() => open()}
              >
                <Plus className="h-4 w-4" />
                Create agent
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Rename Agent Modal ── */}
      {Boolean(renameTarget) && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !renaming) setRenameTarget(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Rename agent"
            className="w-full max-w-md rounded-2xl border border-line bg-paper-card p-6 text-ink shadow-2xl dark:bg-[#292524]"
          >
            <div className="flex items-center gap-2">
              <Pencil className="h-4 w-4 text-brand" />
              <h2 className="text-sm font-bold text-ink sm:text-base">Rename Chatbot Agent</h2>
            </div>
            <p className="mt-1 text-xs text-ink-3">
              Give this WhatsApp AI agent a clear, descriptive title.
            </p>

            <input
              autoFocus
              aria-label="Agent name"
              maxLength={100}
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") confirmRename();
                if (e.key === "Escape" && !renaming) setRenameTarget(null);
              }}
              placeholder="e.g. Sales Assistant"
              className={`${input} mt-4`}
            />

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className={secondary}
                onClick={() => setRenameTarget(null)}
                disabled={renaming}
              >
                Cancel
              </button>
              <button
                type="button"
                className={primary}
                onClick={confirmRename}
                disabled={renaming || !renameName.trim() || renameName.trim() === renameTarget?.name}
              >
                {renaming ? "Renaming…" : "Rename"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDiscardConfirm && (
        <ConfirmModal
          title="Discard unsaved changes?"
          body="You have unsaved changes to this agent. Are you sure you want to discard them?"
          confirmLabel="Discard"
          onClose={() => setShowDiscardConfirm(false)}
          onConfirm={() => {
            setShowDiscardConfirm(false);
            setDraft(null);
            setDirty(false);
          }}
        />
      )}

      {pendingDeleteAgent && (
        <ConfirmModal
          title="Delete agent?"
          body={`Delete "${pendingDeleteAgent.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          onClose={() => setPendingDeleteAgent(null)}
          onConfirm={() => executeDelete(pendingDeleteAgent)}
        />
      )}
    </div>
  );
}
